import React from "react"
import { useForm, useWatch } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Space, Button, message, Checkbox } from "@mimber/ui-web"
import {
  Form,
  InputField,
  SelectField,
  CurrencyInputField,
} from "@mimber/forms"
import { useUpdateMembershipTier } from "@mimber/client/mimber-organization"
import { RecurrenceInterval } from "@mimber/schema"
import type { EditMembershipTierFormProps } from "./EditMembershipTierForm.types"
import {
  editMembershipTierSchema,
  type EditMembershipTierData,
} from "./EditMembershipTierForm.schema"

const INTERVAL_OPTIONS = [
  { label: "Daily", value: RecurrenceInterval.DAILY },
  { label: "Weekly", value: RecurrenceInterval.WEEKLY },
  { label: "Monthly", value: RecurrenceInterval.MONTHLY },
  { label: "Quarterly", value: RecurrenceInterval.QUARTERLY },
  { label: "Yearly", value: RecurrenceInterval.YEARLY },
]

export const EditMembershipTierForm: React.FunctionComponent<
  EditMembershipTierFormProps
> = ({ tier, onSuccess }) => {
  const form = useForm<EditMembershipTierData, unknown, EditMembershipTierData>(
    {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      resolver: zodResolver(editMembershipTierSchema) as any,
      mode: "onBlur",
      defaultValues: {
        name: tier.name,
        description: tier.description || "",
        level: tier.level,
        price: tier.price ?? undefined,
        intervalLength: tier.intervalLength || undefined,
        intervalType: tier.intervalType || undefined,
        isDefault: tier.isDefault,
        isActive: tier.isActive,
      },
    }
  )

  const { control, setValue, watch } = form

  // Watch price field to conditionally show pricing fields
  const priceValue = useWatch({ control, name: "price" })
  const showPricingFields =
    priceValue !== undefined && priceValue !== null && priceValue > 0

  const mutation = useUpdateMembershipTier({
    onSuccess: (response) => {
      message.success("Membership tier updated successfully")
      onSuccess?.(response.data)
    },
    onError: (error) => {
      message.error(`Failed to update membership tier: ${error.message}`)
    },
  })

  const onSubmit = (data: EditMembershipTierData) => {
    // Ensure booleans have defaults
    const processedData = {
      ...data,
      isDefault: data.isDefault ?? false,
      isActive: data.isActive ?? true,
    }
    mutation.mutate({ id: tier.uuid, data: processedData })
  }

  return (
    <Form
      form={form}
      onSubmit={onSubmit}
      disabled={mutation.isPending}
      layout="vertical"
    >
      <InputField
        name="name"
        label="Tier Name"
        placeholder="e.g., Basic, Premium, VIP"
        maxLength={255}
        showCount
      />

      <InputField
        name="description"
        label="Description"
        placeholder="Brief description of the tier benefits"
        maxLength={1000}
        showCount
        multiline
        rows={3}
      />

      <InputField
        name="level"
        label="Tier Level"
        placeholder="1"
        help="Lower numbers indicate lower tier levels. Must be unique per organization."
      />

      <CurrencyInputField
        name="price"
        label="Price"
        minCents={0}
        help="Leave empty for free tiers."
      />

      {showPricingFields && (
        <>
          <InputField
            name="intervalLength"
            label="Billing Interval Length"
            placeholder="e.g., 1, 3, 6"
            required={showPricingFields}
            help="Number of intervals (e.g., 3 for 'every 3 months')"
          />

          <SelectField
            name="intervalType"
            label="Billing Interval Type"
            placeholder="Select interval type"
            required={showPricingFields}
            options={INTERVAL_OPTIONS}
            help="Frequency of recurring billing"
          />
        </>
      )}

      <Space orientation="vertical" size="small" style={{ marginBottom: 16 }}>
        <Checkbox
          checked={watch("isDefault")}
          onChange={(e) => setValue("isDefault", e.target.checked)}
        >
          Set as default tier for new members
        </Checkbox>
        <Checkbox
          checked={watch("isActive")}
          onChange={(e) => setValue("isActive", e.target.checked)}
        >
          Active (available for assignment)
        </Checkbox>
      </Space>

      <Space style={{ width: "100%", justifyContent: "flex-end" }}>
        <Button htmlType="submit" type="primary" loading={mutation.isPending}>
          Update Tier
        </Button>
      </Space>
    </Form>
  )
}
